/// <reference types="vite/client" />
/**
 * /admin — health, suppressions, templates, sequences (U3 + U4 + U5).
 *
 * The admin endpoints require KB_SEED_TOKEN in the Authorization
 * header. The page prompts the operator for the token on mount and
 * stores it in sessionStorage (cleared when the tab closes).
 */

import { useCallback, useEffect, useState } from "react";

interface Health {
  secrets: Record<string, boolean>;
  env_vars: Record<string, string>;
  last_cron_ticks: Array<{ agent: string; action: string; last_seen: string; seconds_ago: number }>;
  d1: { last_migration: string };
  suppression_count?: number;
}

interface Suppression {
  email: string;
  reason: string;
  engagement_id: string | null;
  source: string | null;
  unsubscribed_at: number;
}

interface Template {
  id: string;
  name: string;
  category: string;
  channel: string;
  subject: string | null;
  body_md: string;
  variables: string[];
}

interface SequenceStep {
  delay_hours: number;
  template_id: string;
  channel: string;
  branch_on?: Record<string, unknown>;
}

interface Sequence {
  id: string;
  name: string;
  description: string | null;
  channel: string;
  steps: SequenceStep[];
}

const SK_TOKEN = "levier.admin.token";

function loadToken(): string {
  if (typeof window === "undefined") return "";
  return window.sessionStorage.getItem(SK_TOKEN) ?? "";
}

export function Admin() {
  const [token, setToken] = useState<string>(() => loadToken());
  const [tab, setTab] = useState<"health" | "suppressions" | "library">("health");
  const [health, setHealth] = useState<Health | null>(null);
  const [suppressions, setSuppressions] = useState<Suppression[]>([]);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [sequences, setSequences] = useState<Sequence[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const headers = useCallback(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const load = useCallback(async () => {
    if (!token) return;
    setBusy(true);
    setError(null);
    try {
      if (tab === "health") {
        const res = await fetch("/api/_admin/health", { headers: headers() });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        setHealth(await res.json() as Health);
      } else if (tab === "suppressions") {
        const res = await fetch("/api/_admin/suppressions", { headers: headers() });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const { suppressions: s } = (await res.json()) as { suppressions: Suppression[] };
        setSuppressions(s);
      } else {
        const [tplRes, seqRes] = await Promise.all([
          fetch("/api/_admin/templates", { headers: headers() }),
          fetch("/api/_admin/sequences", { headers: headers() }),
        ]);
        if (!tplRes.ok) throw new Error(`templates HTTP ${tplRes.status}`);
        if (!seqRes.ok) throw new Error(`sequences HTTP ${seqRes.status}`);
        setTemplates(((await tplRes.json()) as { templates: Template[] }).templates);
        setSequences(((await seqRes.json()) as { sequences: Sequence[] }).sequences);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }, [token, tab, headers]);

  useEffect(() => { load(); }, [load]);

  const saveToken = (t: string) => {
    setToken(t);
    if (typeof window !== "undefined") window.sessionStorage.setItem(SK_TOKEN, t);
  };

  const lift = async (email: string) => {
    if (!confirm(`Réintégrer ${email} dans les envois ?`)) return;
    setBusy(true);
    try {
      const res = await fetch(`/api/_admin/suppressions/${encodeURIComponent(email)}`, { method: "DELETE", headers: headers() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  if (!token) {
    return (
      <div className="space-y-6">
        <header className="reveal">
          <p className="eyebrow">Console · 12</p>
          <h2 className="font-display italic text-3xl sm:text-4xl lg:text-[56px] leading-[0.95] mt-2 tracking-tight">
            <span className="text-[var(--color-text)]">Admin </span>
            <span className="text-accent-gradient">/ Health</span>
          </h2>
        </header>
        <div className="panel p-5 sm:p-6 max-w-md w-full">
          <p className="eyebrow mb-3">KB_SEED_TOKEN requis</p>
          <p className="text-sm text-[var(--color-text-muted)] mb-3">
            Les endpoints /api/_admin/* sont gated par le secret KB_SEED_TOKEN. Colle-le ici (stocké en sessionStorage, supprimé à la fermeture de l'onglet).
          </p>
          <input
            type="password"
            placeholder="kb_seed_token..."
            onKeyDown={(e) => { if (e.key === "Enter") saveToken((e.target as HTMLInputElement).value); }}
            className="w-full h-12 px-3 rounded font-mono text-base sm:text-sm"
            style={{ background: "var(--color-bg-card)", color: "var(--color-text)", border: "1px solid var(--color-border)" }}
          />
          <p className="font-mono text-[10px] text-[var(--color-text-dim)] mt-2">Press Enter to save</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 lg:space-y-8">
      <header className="reveal flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="eyebrow">Console · 12</p>
          <h2 className="font-display italic text-3xl sm:text-4xl lg:text-[56px] leading-[0.95] mt-2 tracking-tight">
            <span className="text-[var(--color-text)]">Admin </span>
            <span className="text-accent-gradient">/ Health</span>
          </h2>
        </div>
        <button
          onClick={() => { saveToken(""); }}
          className="font-mono text-[10px] uppercase tracking-wider text-[var(--color-text-dim)] hover:text-[var(--color-accent)]"
        >
          Forget token
        </button>
      </header>

      <div className="flex gap-2 font-mono text-xs uppercase tracking-wider overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0 snap-x">
        {(["health", "suppressions", "library"] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className="shrink-0 snap-start px-3 py-2 rounded transition-colors min-h-[36px]"
            style={{
              background: tab === t ? "var(--color-accent-soft)" : "var(--color-bg-card)",
              color: tab === t ? "var(--color-accent-glow)" : "var(--color-text-muted)",
              border: `1px solid ${tab === t ? "var(--color-accent)" : "var(--color-border)"}`,
            }}
          >
            {t}
          </button>
        ))}
      </div>

      {error && (
        <div className="panel p-3" style={{ borderColor: "var(--color-status-error)", background: "rgba(239,68,68,0.04)" }}>
          <p className="font-mono text-xs text-[var(--color-status-error)]">{error}</p>
        </div>
      )}

      {busy && !health && <p className="font-mono text-xs text-[var(--color-text-dim)] blink">Loading…</p>}

      {tab === "health" && health && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="panel p-4 sm:p-5">
            <p className="eyebrow mb-3">Secrets</p>
            <ul className="space-y-1.5">
              {Object.entries(health.secrets).map(([k, v]) => (
                <li key={k} className="flex items-center justify-between font-mono text-xs">
                  <span className="text-[var(--color-text)]">{k}</span>
                  <span style={{ color: v ? "var(--color-status-active)" : "var(--color-status-error)" }}>
                    {v ? "✓ set" : "✗ missing"}
                  </span>
                </li>
              ))}
            </ul>
          </div>
          <div className="panel p-5">
            <p className="eyebrow mb-3">Env vars</p>
            <ul className="space-y-1.5">
              {Object.entries(health.env_vars).map(([k, v]) => (
                <li key={k} className="flex items-center justify-between font-mono text-xs">
                  <span className="text-[var(--color-text)]">{k}</span>
                  <span className="text-[var(--color-text-muted)]">{v}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="panel p-4 sm:p-5 md:col-span-2">
            <p className="eyebrow mb-3">Last cron ticks</p>
            {health.last_cron_ticks.length === 0 ? (
              <p className="font-mono text-xs text-[var(--color-text-dim)]">Aucun tick enregistré</p>
            ) : (
              <ul className="space-y-1.5">
                {health.last_cron_ticks.map((t) => (
                  <li key={`${t.agent}-${t.action}`} className="flex items-center justify-between font-mono text-xs">
                    <span className="text-[var(--color-text)]">{t.agent} · {t.action}</span>
                    <span
                      className="text-[var(--color-text-muted)] tabular-nums"
                      style={{ color: t.seconds_ago > 14400 ? "var(--color-status-error)" : "var(--color-text-muted)" }}
                    >
                      {t.seconds_ago < 60 ? `${t.seconds_ago}s` : t.seconds_ago < 3600 ? `${Math.floor(t.seconds_ago / 60)}m` : `${Math.floor(t.seconds_ago / 3600)}h`} ago
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="panel p-4 sm:p-5 md:col-span-2">
            <p className="eyebrow mb-2">D1</p>
            <p className="font-mono text-xs">Last migration: <span className="text-[var(--color-text-muted)]">{health.d1.last_migration}</span></p>
            {typeof health.suppression_count === "number" && (
              <p className="font-mono text-xs mt-1">Suppression count: <span className="text-[var(--color-text-muted)]">{health.suppression_count}</span></p>
            )}
          </div>
        </div>
      )}

      {tab === "suppressions" && (
        <div className="panel p-5">
          <p className="eyebrow mb-3">Email suppressions ({suppressions.length})</p>
          {suppressions.length === 0 ? (
            <p className="font-mono text-xs text-[var(--color-text-dim)]">Aucune désinscription</p>
          ) : (
            <ul className="space-y-1.5">
              {suppressions.map((s) => (
                <li key={s.email} className="flex items-center gap-3 font-mono text-xs">
                  <span className="text-[var(--color-text)] flex-1 truncate">{s.email}</span>
                  <span className="text-[var(--color-text-dim)]">{s.reason}</span>
                  <span className="text-[var(--color-text-dim)] tabular-nums">{new Date(s.unsubscribed_at * 1000).toLocaleDateString("fr-FR")}</span>
                  <button
                    onClick={() => lift(s.email)}
                    disabled={busy}
                    className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded"
                    style={{ color: "var(--color-status-error)", border: "1px solid rgba(239,68,68,0.4)" }}
                  >
                    Lift
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {tab === "library" && (
        <div className="space-y-6">
          <div>
            <p className="eyebrow mb-3">Templates ({templates.length})</p>
            <div className="space-y-2">
              {templates.map((t) => (
                <details key={t.id} className="panel p-3">
                  <summary className="cursor-pointer flex items-center gap-2">
                    <span className="font-mono text-xs text-[var(--color-text)]">{t.id}</span>
                    <span className="pill" style={{ color: "var(--color-text-muted)", borderColor: "var(--color-border)", background: "var(--color-bg-card)" }}>
                      {t.category}
                    </span>
                    <span className="pill" style={{ color: "var(--color-text-muted)", borderColor: "var(--color-border)", background: "var(--color-bg-card)" }}>
                      {t.channel}
                    </span>
                    <span className="font-mono text-[10px] text-[var(--color-text-dim)] ml-auto">vars: {t.variables.join(", ")}</span>
                  </summary>
                  <div className="mt-2 space-y-2">
                    {t.subject && <p className="font-mono text-xs text-[var(--color-text-muted)]">Subject: {t.subject}</p>}
                    <pre className="text-sm whitespace-pre-wrap p-2 rounded" style={{ background: "var(--color-bg-card)", color: "var(--color-text)", border: "1px solid var(--color-border)" }}>
                      {t.body_md}
                    </pre>
                  </div>
                </details>
              ))}
            </div>
          </div>
          <div>
            <p className="eyebrow mb-3">Sequences ({sequences.length})</p>
            <div className="space-y-2">
              {sequences.map((s) => (
                <div key={s.id} className="panel p-3">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs text-[var(--color-text)]">{s.id}</span>
                    <span className="pill" style={{ color: "var(--color-text-muted)", borderColor: "var(--color-border)", background: "var(--color-bg-card)" }}>
                      {s.channel}
                    </span>
                    <span className="font-mono text-[10px] text-[var(--color-text-dim)] ml-auto">{s.steps.length} steps</span>
                  </div>
                  {s.description && <p className="text-xs text-[var(--color-text-muted)] mt-1">{s.description}</p>}
                  <div className="mt-2 space-y-1">
                    {s.steps.map((step, i) => (
                      <div key={i} className="flex items-center gap-2 font-mono text-[11px]">
                        <span className="text-[var(--color-accent-bright)]">step {i + 1}</span>
                        <span className="text-[var(--color-text-muted)]">+{step.delay_hours}h</span>
                        <span className="text-[var(--color-text)]">{step.channel}</span>
                        <span className="text-[var(--color-text-dim)]">→ {step.template_id}</span>
                        {step.branch_on && (
                          <span className="text-[10px] text-[var(--color-text-dim)] ml-auto">
                            {Object.entries(step.branch_on).map(([k, v]) => `${k}:${JSON.stringify(v)}`).join(" · ")}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
